import React from 'react'
import { useNavigate } from 'react-router-dom'
import PageShell from '../../components/PageShell/PageShell'
import { AboutArt } from '../../components/PageShell/HeroArt'
import {
    IconBolt, IconShield, IconDevices, IconExport, IconApprove, IconTeam,
} from '../../components/HomeContent/Icons'

const VALUES = [
    { icon: <IconBolt />, title: 'Admin should cost minutes', copy: 'If filling in a report takes longer than doing the thing you are reporting, the tool has failed. Every screen is built against that rule.' },
    { icon: <IconTeam />, title: 'Built for the whole team', copy: 'The person logging their day matters as much as the manager reading the summary. Neither gets a worse experience than the other.' },
    { icon: <IconShield />, title: 'Boundaries by default', copy: 'People see their own work, managers see their team. Access follows the org chart rather than an honour system.' },
    { icon: <IconApprove />, title: 'Nothing waits on a nudge', copy: 'Requests route themselves, queues clear in one place, and the calendar answers "who is out?" before anyone asks.' },
    { icon: <IconDevices />, title: 'Wherever the work happens', copy: 'Office, home, or between meetings on a phone. Same screen, nothing to install, no separate mobile app to keep in sync.' },
    { icon: <IconExport />, title: 'Your data is yours', copy: 'Every report exports in a click, in a format a spreadsheet can read. No lock-in, no export fee, no support ticket.' },
];

const About = () => {
    const navigate = useNavigate();

    return (
        <PageShell
            eyebrow="About us"
            title="We build the boring part, properly"
            lede="WorkHub is a work management platform for startups and mid-sized teams — reports, timesheets, leave, projects, hiring and assets in one place."
            art={<AboutArt />}
        >
            {/* story */}
            <section className="pg-sec pg-sec--light">
                <div className="container">
                    <div className="pg-split">
                        <div>
                            <p className="pg-eyebrow">Why we built it</p>
                            <h2 className="pg-title pg-title--grad">Every team rebuilds the same spreadsheet</h2>
                            <div className="pg-body">
                                <p>
                                    Most growing companies run their week across half a dozen places at once. Hours
                                    live in one spreadsheet, leave in another, project status in a chat thread,
                                    and candidate feedback in somebody's inbox.
                                </p>
                                <p>
                                    It works, until it does not. Someone leaves and takes the spreadsheet's logic
                                    with them. A manager spends Friday afternoon chasing four people for updates.
                                    A leave balance turns out to have been wrong for two quarters.
                                </p>
                                <p>
                                    WorkHub exists to put all of it in one place, with defaults sensible enough
                                    that nobody needs a training day to use it.
                                </p>
                            </div>
                        </div>
                        <ul className="pg-checks" style={{ paddingTop: '8px' }}>
                            <li>One login for reports, hours, leave, projects, hiring and assets</li>
                            <li>Configured once to your policy, then it holds the line</li>
                            <li>Set up in an afternoon, not a six-week onboarding</li>
                            <li>Works on a phone as well as it does on a laptop</li>
                            <li>Everything exportable, whenever you want it</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* values */}
            <section className="pg-sec pg-sec--wash">
                <div className="container">
                    <header className="pg-head pg-head--center">
                        <p className="pg-eyebrow">What we believe</p>
                        <h2 className="pg-title">Six rules we build against</h2>
                    </header>
                    <ul className="pg-grid">
                        {VALUES.map((v) => (
                            <li className="pg-card" key={v.title}>
                                <span className="pg-icon">{v.icon}</span>
                                <h3 className="pg-h3">{v.title}</h3>
                                <p>{v.copy}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* closing */}
            <section className="pg-sec pg-sec--tint">
                <div className="container">
                    <header className="pg-head pg-head--center">
                        <p className="pg-eyebrow">Come and look</p>
                        <h2 className="pg-title pg-title--grad">The fastest way to judge it is to use it</h2>
                        <p className="pg-lede">
                            Set your team up today and have your first full week of reports by Friday.
                        </p>
                    </header>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
                        <button type="button" className="pg-btn pg-btn--primary" onClick={() => navigate('/login')}>
                            Get Started
                        </button>
                        <button type="button" className="pg-btn pg-btn--ghost" onClick={() => navigate('/contact')}>
                            Talk to us
                        </button>
                    </div>
                </div>
            </section>
        </PageShell>
    )
}

export default About
